// hooks/useRouteScheduling.js
import { useCallback, useEffect, useMemo, useRef, useReducer } from 'react';
import moment from 'moment';
import { 
  setLoading, 
  setRoutes, 
  setCrews, 
  setWeeklyHours,
  setUnassignedJobs,
  setFilteredMarkers,
  setActiveMarker,
  updateUI,
  updateJobDetails,
  assignJobSuccess,
  unassignJobSuccess,
  selectCrew
} from '../state/routeActions';
import { ROUTE_CONFIG } from '../config/routeConfig';
import { RouteCache, createRouteCacheKey, createSiteCacheKey } from '../utils/cacheUtils';
import { isInCurrentWeek, findNextMatchingDate, shouldScheduleJob } from '../utils/dateUtils';
import { calculateDistance, extractCoordinates, getMapOffset } from '../utils/geoUtils';
import { validateRouteHours, validateWeeklyHours } from '../utils/hoursValidation';
import { isRegularFrequency, isScheduledFrequency } from '../config/routeConfig';
import { RouteService } from '../services/RouteService';
import { CrewService } from '../services/CrewService';
import { NotificationService } from '../services/NotificationService';
import { routeReducer, initialState } from '../state/routeReducer';
import { useJsApiLoader } from '@react-google-maps/api';
import { clearRouteCache, clearExpiredCache, clearSiteCache } from '../state/routeActions';
import { useSSEAssignment } from './useSSEAssignment';

export const useRouteScheduling = (props) => {
  // console.log('useRouteScheduling...', props)
  // Services can be created from props
  const routeService = useMemo(() => new RouteService(props.url), [props.url]);
  const crewService = useMemo(() => new CrewService(props.url), [props.url]);
  const notificationService = useMemo(() => new NotificationService(props.notification), 
    [props.notification]
  );
  const crews = props?.crews || []
  // const notificationService = useRef(new NotificationService(props.notification));
  const [state, dispatch] = useReducer(routeReducer, {
    ...initialState,
    crews
  });
  
  // SSE Assignment hook
  const { startAssignment, endAssignment, progress: sseProgress, isAssigning } = useSSEAssignment(
    props.url,
    notificationService
  );
  
  // Track pending operations to prevent conflicts
  const pendingOperations = useRef(new Set());
  const refreshTimeoutRef = useRef(null);
  
  // Extract state values for easier access
  const {
    loading,
    selectedDate,
    // crews,
    sites,
    weeklyRoutes,
    weeklyHours,
    filters,
    ui,
    cache,
    jobSiteDetails
  } = state;

  // Process unassigned jobs
  const unassignedJobs = useMemo(() => {
    if (!sites.length) return [];
    
    const dayOfWeek = moment(selectedDate).format('dddd');
    const weekType = moment(selectedDate).week() % 2 === 0 ? 'Even' : 'Odd';
    
    return sites.filter(site => {
      // Check if it's unrouted
      if (site.unrouted) return true;
      
      // Check if it's unassigned
      if (!site.crew) {
        // For scheduled frequencies, check if it has future visits
        if (isScheduledFrequency(site.frequency)) {
          const hasFutureVisits = cache.futureVisits?.some(
            visit => visit.siteId === site._id && new Date(visit.visitDate) >= new Date()
          );
          return !hasFutureVisits;
        }
        // For regular frequencies, it's unassigned
        return true;
      }
      
      return false;
    });
  }, [sites, selectedDate, cache.futureVisits]);
  
  // Filter markers based on selected crew and date
  const filteredMarkers = useMemo(() => {
    const markers = [];
    const dayOfWeek = moment(selectedDate).format('dddd');
    const selectedDateString = moment(selectedDate).format('YYYY-MM-DD');
    
    // Add route stops for the day
    if (weeklyRoutes.length > 0) {
      const routesForDay = weeklyRoutes.find(route => route.day === dayOfWeek);
      
      if (routesForDay) {
        routesForDay.routes.forEach(route => {
          const crew = crews.find(c => c._id === route.crewId);
          route.stops?.forEach((stop, index) => {
            const site = sites.find(s => s._id === stop.siteId);
            if (site) {
              markers.push({
                ...site,
                ...stop,
                crew: route.crewId,
                crewName: route.crewName,
                color: crew?.color,
                orderNumber: index + 1,
                markerType: 'route'
              });
            }
          });
        });
      }
    }
    
    // Add future visits for the selected date
    if (cache.futureVisits) {
      cache.futureVisits.forEach(visit => {
        const visitDate = moment(visit.visitDate).format('YYYY-MM-DD');
        if (visitDate === selectedDateString) {
          const site = sites.find(s => s._id === visit.siteId);
          if (site && !markers.find(m => m._id === site._id)) {
            const crew = crews.find(c => c._id === site.crew);
            markers.push({
              ...site,
              color: crew?.color || '#ff9800', // Orange for scheduled visits
              crewName: crew?.name || 'Scheduled',
              isScheduledVisit: true,
              markerType: 'futureVisit',
              visitDate: visit.visitDate
            });
          }
        }
      });
    }
    
    return markers;
  }, [weeklyRoutes, selectedDate, sites, crews, cache.futureVisits]);
  
  // All sites markers for "All Sites" tab view
  const allSitesMarkers = useMemo(() => {
    if (!sites.length) return [];
    
    return sites.map(site => {
      const crew = crews.find(c => c._id === site.crew);
      return {
        ...site,
        color: crew?.color || '#ff5722', // Orange for unassigned
        crewName: crew?.name || 'Unassigned',
        isUnassigned: !site.crew
      };
    });
  }, [sites, crews]);
  
  // Active route details (when adding a job to a route)
  const activeRouteDetails = useRef({});
  
  // Fetch routes for a specific date
  const fetchRoutes = useCallback(async (date, forceRefresh = false) => {
    console.log('🔍 fetchRoutes called:', {
      date: moment(date).format('YYYY-MM-DD'),
      forceRefresh,
      currentWeekStart: cache.weekStart ? moment(cache.weekStart).format('YYYY-MM-DD') : null,
      currentWeekEnd: cache.weekEnd ? moment(cache.weekEnd).format('YYYY-MM-DD') : null,
      stack: new Error().stack
    })
    
    // FIRST: Check if date is in current week range - if so, we already have ALL data
    if (!forceRefresh && cache.weekStart && cache.weekEnd) {
      if (isInCurrentWeek(date, cache.weekStart, cache.weekEnd)) {
        console.log('📅 Date is in current week range, using existing data - NO FETCH NEEDED');
        dispatch(updateUI({ selectedDate: date })); // Still update the selected date
        return; // Data already loaded for ALL crews
      }
    }
    
    // Create cache key based on WEEK, not crew (since API returns all crews for the week)
    const cacheKey = createRouteCacheKey(date, null); // Don't include crew ID
  
    // Check cache from state
    if (!forceRefresh && cache.routes[cacheKey]) {
      const cachedData = cache.routes[cacheKey];
      const age = Date.now() - cachedData.timestamp;
      
      if (age < ROUTE_CONFIG.CACHE.ROUTE_TTL) {
        console.log('Using cached data for week');
        dispatch(setRoutes(cachedData.data));
        return;
      }
    }
    
    dispatch(setLoading(true));
    
    try {
      const routeData = await routeService.fetchWeeklyRoutes(date);
      console.log('got updated route data...', routeData)
      // Process crews if included in route data
      if (routeData.sites?.length) {
        // Use crews from props (Redux) for processing
        const crewsToProcess = crews?.length ? crews : state.crews;
        const processedCrews = crewService.processCrews(crewsToProcess);
        dispatch(setCrews(processedCrews));
        
        // Calculate weekly hours
        // Validate route data before calculating hours
        routeData.routes.forEach(dayRoute => {
          dayRoute.routes.forEach(route => {
            validateRouteHours(route);
          });
        });
        
        const calculatedHours = crewService.calculateWeeklyHours(
          routeData.routes,
          processedCrews
        );
        console.log('Calculated Hours...', calculatedHours);
        
        // Validate the calculated hours
        validateWeeklyHours(calculatedHours, routeData.routes);
        
        dispatch(setWeeklyHours(calculatedHours));
      }
      
      // Cache the result
      dispatch({
        type: 'CACHE_ROUTE_DATA',
        payload: {
          key: cacheKey,
          data: routeData
        }
      });
      
      // Update state
      dispatch(setRoutes(routeData));
      
    } catch (error) {
      notificationService.error(error.message || ROUTE_CONFIG.ERRORS.ROUTE_FETCH_FAILED);
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, routeService, notificationService, crews, cache.routes, cache.weekStart, cache.weekEnd]);

  const handleAssignToCrew = useCallback(async (assignmentData) => {
    // Prevent duplicate operations
    const operationKey = `assign-${assignmentData.site._id}-${assignmentData.crew._id}`;
    if (pendingOperations.current.has(operationKey)) {
      console.warn('Assignment already in progress for this site/crew');
      return;
    }
    console.log('handleAssignToCrew', assignmentData)
    pendingOperations.current.add(operationKey);
    
    // Start SSE connection for real-time updates
    console.log('🚀 Starting SSE connection...');
    const sessionId = await startAssignment();
    console.log('🚀 SSE session ID:', sessionId);
    
    if (!sessionId) {
      console.error('🚀 Failed to get SSE session ID');
    }
    
    dispatch(setLoading(true));
    
    // Store original data for rollback on error
    const originalSites = state.sites;
    const originalWeeklyRoutes = weeklyRoutes;
    console.log('state.sites...', originalSites)
    try {
      // 1. Update sites array immediately with crew assignment
      const updatedSites = state.sites.map(site => 
        site._id === assignmentData.site._id 
          ? { ...site, crew: assignmentData.crew._id, unrouted: false }
          : site
      );
      
      // 2. Update or create crew route for the day
      const dayOfWeek = moment(selectedDate).format('dddd');
      let updatedWeeklyRoutes = [...weeklyRoutes];
      
      // Find or create route for the day
      let dayRouteIndex = updatedWeeklyRoutes.findIndex(r => r.day === dayOfWeek);
      if (dayRouteIndex === -1) {
        updatedWeeklyRoutes.push({
          day: dayOfWeek,
          routes: []
        });
        dayRouteIndex = updatedWeeklyRoutes.length - 1;
      }
      
      // Find or create crew route
      let crewRoute = updatedWeeklyRoutes[dayRouteIndex].routes.find(
        r => r.crewId === assignmentData.crew._id
      );
      
      if (!crewRoute) {
        // Create new route for crew if they have no jobs today
        crewRoute = {
          crewId: assignmentData.crew._id,
          crewName: assignmentData.crew.name,
          stops: [],
          totalServiceTime: 0,
          totalDriveTime: 0,
          totalMilesDriven: 0
        };
        updatedWeeklyRoutes[dayRouteIndex].routes.push(crewRoute);
      }
      
      // 3. Create the new stop
      const newStop = {
        siteId: assignmentData.site._id,
        name: assignmentData.site.name,
        latitude: assignmentData.site.latitude,
        longitude: assignmentData.site.longitude,
        estimatedHours: assignmentData.site.estimatedHours || 1,
        serviceCode: assignmentData.site.serviceCode,
        serviceName: assignmentData.site.serviceName,
        order: crewRoute.stops.length + 1
      };
      
      // 4. Update the crew's route with optimized data if available
      const crewRouteIndex = updatedWeeklyRoutes[dayRouteIndex].routes.findIndex(
        r => r.crewId === assignmentData.crew._id
      );
      
      if (activeRouteDetails.current?.stops && activeRouteDetails.current.calculatedForSite === assignmentData.site._id) {
        // Use optimized route data
        updatedWeeklyRoutes[dayRouteIndex].routes[crewRouteIndex] = {
          ...updatedWeeklyRoutes[dayRouteIndex].routes[crewRouteIndex],
          stops: activeRouteDetails.current.stops,
          totalMilesDriven: activeRouteDetails.current.totalMilesDriven || 0,
          totalDriveTime: activeRouteDetails.current.totalDriveTime || 0,
          totalServiceTime: activeRouteDetails.current.totalServiceTime || 0
        };
      } else {
        // Just append the new stop
        const currentRoute = updatedWeeklyRoutes[dayRouteIndex].routes[crewRouteIndex];
        const additionalServiceMinutes = (assignmentData.site.estimatedHours || 1) * 60;
        
        console.log('🔧 Adding stop to route:', {
          crew: assignmentData.crew.name,
          site: assignmentData.site.name,
          currentServiceTime: currentRoute.totalServiceTime,
          additionalMinutes: additionalServiceMinutes,
          newTotal: currentRoute.totalServiceTime + additionalServiceMinutes
        });
        
        updatedWeeklyRoutes[dayRouteIndex].routes[crewRouteIndex] = {
          ...currentRoute,
          stops: [...currentRoute.stops, newStop],
          totalServiceTime: currentRoute.totalServiceTime + additionalServiceMinutes
          // Note: totalDriveTime would need to be recalculated based on route optimization
        };
      }
      
      // 5. Recalculate weekly hours after the route update
      const recalculatedHours = crewService.calculateWeeklyHours(
        updatedWeeklyRoutes,
        crews
      );
      
      // 6. Apply optimistic updates immediately to show in UI
      dispatch(setRoutes({ 
        routes: updatedWeeklyRoutes, 
        sites: updatedSites,
        weekStart: state.cache.weekStart,
        weekEnd: state.cache.weekEnd,
        futureVisits: state.cache.futureVisits
      }));
      
      // Update weekly hours with recalculated values
      dispatch(setWeeklyHours(recalculatedHours));
      
      // 6. Make the API call
      const result = await routeService.assignSiteToCrew({
        siteId: assignmentData.site._id,
        crewId: assignmentData.crew._id,
        scheduledDates: assignmentData.scheduledDates,
        selectedDate: selectedDate.toISOString(),
        sessionId // Include session ID for SSE updates
      });
      
      // 7. Success - dispatch success action (cleans up activeMarker, etc)
      dispatch(assignJobSuccess({
        siteId: assignmentData.site._id,
        crewId: assignmentData.crew._id,
        scheduledDates: assignmentData.scheduledDates,
        frequency: assignmentData.site.frequency
      }));
      
      notificationService.success(ROUTE_CONFIG.SUCCESS.JOB_ASSIGNED);
      
      // 8. Clear route details reference
      activeRouteDetails.current = null;
      
      // 9. Delayed cache clear and refresh to give backend time to process
      // Clear any existing timeout
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      
      refreshTimeoutRef.current = setTimeout(() => {
        dispatch(clearRouteCache());
        dispatch(clearSiteCache(assignmentData.site._id));
        // Refresh with latest data from backend
        fetchRoutes(selectedDate, true);
        refreshTimeoutRef.current = null;
      }, 2000); // 2 second delay
      
    } catch (error) {
      // 10. On error, rollback the optimistic update
      dispatch(setRoutes({ 
        routes: originalWeeklyRoutes, 
        sites: originalSites,
        weekStart: state.cache.weekStart,
        weekEnd: state.cache.weekEnd,
        futureVisits: state.cache.futureVisits
      }));
      
      notificationService.error(error.message || ROUTE_CONFIG.ERRORS.ASSIGNMENT_FAILED);
    } finally {
      dispatch(setLoading(false));
      pendingOperations.current.delete(operationKey);
      // End SSE connection
      endAssignment();
    }
  }, [dispatch, routeService, notificationService, selectedDate, weeklyRoutes, state.sites, state.cache, activeRouteDetails, fetchRoutes, startAssignment, endAssignment]);

  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(clearExpiredCache());
    }, 60000); // Every minute
    
    return () => {
      clearInterval(interval);
      // Clean up any pending refresh timeout
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [dispatch]);

  // Handle job unassignment
  const handleUnassignFromCrew = useCallback(async (site, crew, date) => {
    dispatch(setLoading(true));
    console.log('handleUnassignFromCrew...', site)
    let siteId = site?.siteId || site._id
    console.log('state.sites', state.sites)
    
    // If crew is not provided, find it from the site data or routes
    let assignedCrew = crew;
    if (!assignedCrew) {
      // First check if site has crew info
      if (site.crew) {
        assignedCrew = crews.find(c => c._id === site.crew);
      }
      
      // If not found, search through all routes to find which crew has this site
      if (!assignedCrew) {
        for (const dayRoute of weeklyRoutes) {
          for (const route of dayRoute.routes) {
            if (route.stops.some(stop => stop.siteId === siteId)) {
              assignedCrew = crews.find(c => c._id === route.crewId);
              if (assignedCrew) break;
            }
          }
          if (assignedCrew) break;
        }
      }
      
      if (!assignedCrew) {
        throw new Error('Could not find assigned crew for this site');
      }
    }
    
    // Store original data for rollback
    const originalSites = state.sites;
    const originalWeeklyRoutes = weeklyRoutes;
    
    try {
      // 1. Optimistically update sites array - remove crew assignment
      const updatedSites = state.sites.map(s => 
        s._id === siteId 
          ? { ...s, crew: null, unrouted: true }
          : s
      );
      console.log('Updated sites...', updatedSites)
      
      // 2. Optimistically update routes - remove stop from ALL routes that have this site
      const updatedWeeklyRoutes = weeklyRoutes.map(routeDay => {
        return {
          ...routeDay,
          routes: routeDay.routes.map(route => {
            // Check if this route has the site
            if (route.stops.some(stop => stop.siteId === siteId)) {
              // Remove the stop and recalculate totals
              const filteredStops = route.stops.filter(stop => stop.siteId !== siteId);
              const removedHours = (site.estimatedHours || 1) * 60;
              
              return {
                ...route,
                stops: filteredStops,
                totalServiceTime: Math.max(0, route.totalServiceTime - removedHours)
                // Drive time and miles would need recalculation by backend
              };
            }
            return route;
          })
        };
      });
      
      // 3. Recalculate weekly hours after removing the stop
      const recalculatedHours = crewService.calculateWeeklyHours(
        updatedWeeklyRoutes,
        crews
      );
      
      // 4. Apply optimistic update
      dispatch(setRoutes({ 
        routes: updatedWeeklyRoutes, 
        sites: updatedSites,
        weekStart: state.cache.weekStart,
        weekEnd: state.cache.weekEnd,
        futureVisits: state.cache.futureVisits
      }));
      
      // Update weekly hours with recalculated values
      dispatch(setWeeklyHours(recalculatedHours));
      
      // 4. Make API call
      const result = await routeService.unassignSiteFromCrew({
        siteId: siteId,
        crewId: assignedCrew._id,
        date: (date || selectedDate).toISOString()
      });
      
      // 5. Success
      dispatch(unassignJobSuccess({ site }));
      notificationService.success(ROUTE_CONFIG.SUCCESS.JOB_UNASSIGNED);
      
      // 6. Delayed refresh to get updated route optimization from backend
      setTimeout(() => {
        fetchRoutes(date, true);
      }, 2000);
      
    } catch (error) {
      // 7. Rollback on error
      dispatch(setRoutes({ 
        routes: originalWeeklyRoutes, 
        sites: originalSites,
        weekStart: state.cache.weekStart,
        weekEnd: state.cache.weekEnd,
        futureVisits: state.cache.futureVisits
      }));
      
      notificationService.error(error.message || ROUTE_CONFIG.ERRORS.UNASSIGNMENT_FAILED);
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, routeService, notificationService, fetchRoutes, state.sites, weeklyRoutes, state.cache, crews, selectedDate]);
  
  // Get details about a specific site
  const getDetailsAboutSite = useCallback(async (site) => {
    const cacheKey = createSiteCacheKey(site._id);
    
    // Check cache from state
    if (cache.siteDetails[cacheKey]) {
      const cachedDetails = cache.siteDetails[cacheKey];
      const age = Date.now() - cachedDetails.timestamp;
      
      if (age < ROUTE_CONFIG.CACHE.SITE_DETAILS_TTL) {
        dispatch(updateJobDetails(cachedDetails.data));
        return cachedDetails.data;
      }
    }
    console.log('272...get details', site)
    try {
      const details = await routeService.getNearestSiteDetails(site);
      console.log('278...', details)
      // Process nearest sites
      if (details.nearestSites?.length) {
        console.log('got some nearest sites...', details.nearestSites.length)
        details.nearestSites = details.nearestSites
          .filter(nearestSite => nearestSite._id !== site._id)
          .map(nearestSite => {
            const fullSite = sites.find(s => s._id === nearestSite._id);
            if (!fullSite) return null;
            
            const crew = state.crews.find(c => c._id === fullSite.crew);
            return {
              ...fullSite,
              ...nearestSite,
              distance: nearestSite.distance,
              color: crew?.color,
              crewName: crew ? crewService.formatCrewName(crew) : 'Unassigned'
            };
          })
          .filter(Boolean);
      }
      
      // Calculate next suggested date
      if (details.lastWorkorder?.scheduledDate) {
        details.nextSuggestedDate = findNextMatchingDate(site, new Date(details.lastWorkorder.scheduledDate));
      }
      console.log('304...details', details)
      // Cache using dispatch  
      dispatch({
        type: 'CACHE_SITE_DETAILS',
        payload: {
          siteId: site._id,
          data: details
        }
      })  
      dispatch(updateJobDetails(details));
      return details;
    } catch (error) {
      console.error('Failed to get site details:', error);
      return null;
    }
  }, [dispatch, routeService, sites, state.crews, cache.siteDetails]);

  // Calculate route with a new site added
  const calculateRouteWithSite = useCallback(async (existingStops, newSite) => {
    console.log('🚗 calculateRouteWithSite called with:', {
      existingStops: existingStops.length,
      newSite: newSite.name,
      newSiteId: newSite._id
    });
    
    try {
      // Prepare all stops including the new site
      const allStops = [...existingStops, {
        ...newSite,
        isNew: true,
        order: existingStops.length + 1
      }];
      
      // Get the current route data for the selected crew and day
      const dayOfWeek = moment(selectedDate).format('dddd');
      const todaysRoutes = weeklyRoutes.find(r => r.day === dayOfWeek);
      const crewRoute = todaysRoutes?.routes?.find(r => r.crewId === filters.selectedCrew?._id);
      
      console.log('🚗 Current crew route:', {
        crewId: filters.selectedCrew?._id,
        currentMiles: crewRoute?.totalMilesDriven || 0,
        currentStops: crewRoute?.stops?.length || 0
      });
      
      const optimizedRoute = await routeService.optimizeRoute(allStops, crewRoute);
      
      if (optimizedRoute) {
        console.log('🚗 Route optimization successful:', optimizedRoute);
        
        // Store the optimized route details
        activeRouteDetails.current = {
          ...optimizedRoute,
          originalMiles: crewRoute?.totalMilesDriven || 0,
          calculatedForSite: newSite._id
        };
        
        // Force a re-render by updating state
        dispatch({
          type: 'UPDATE_UI',
          payload: { 
            activeRouteDetailsTimestamp: Date.now() // Force update
          }
        });
        
        return optimizedRoute;
      }
      
      console.warn('🚗 No optimized route returned');
      return null;
    } catch (error) {
      console.error('🚗 Failed to optimize route:', error);
      notificationService.warning('Route optimization failed. Using rough estimation.');
      
      // Fallback: Calculate a rough estimate
      const roughEstimate = calculateRoughMileageEstimate(existingStops, newSite);
      activeRouteDetails.current = {
        totalMilesDriven: roughEstimate.totalMiles,
        isEstimate: true,
        calculatedForSite: newSite._id
      };
      
      return roughEstimate;
    }
  }, [routeService, notificationService, selectedDate, weeklyRoutes, filters.selectedCrew, dispatch]);
  
  // Helper function for rough mileage estimation
  const calculateRoughMileageEstimate = (existingStops, newSite) => {
    console.log('🚗 Calculating rough mileage estimate');
    
    if (!existingStops.length) {
      // If no existing stops, estimate round trip from yard
      return { 
        totalMiles: 20, // Default 10 miles each way
        isEstimate: true 
      };
    }
    
    // Find the closest existing stop
    let minDistance = Infinity;
    let totalDistance = 0;
    let validDistances = 0;
    
    existingStops.forEach(stop => {
      const distance = calculateDistance(newSite, stop);
      if (distance > 0 && distance < 1000) { // Sanity check - ignore unrealistic distances
        totalDistance += distance;
        validDistances++;
        if (distance < minDistance) {
          minDistance = distance;
        }
      }
    });
    
    // Use the minimum distance for a conservative estimate
    // Multiply by 2 for round trip impact
    const estimatedAdditionalMiles = minDistance < Infinity ? 
      Math.round(minDistance * 2) : 
      15; // Default 15 miles if we can't calculate
    
    console.log('🚗 Rough estimate:', {
      minDistance,
      estimatedAdditionalMiles,
      validDistances
    });
    
    // Get current miles from the crew's route
    const dayOfWeek = moment(selectedDate).format('dddd');
    const todaysRoutes = weeklyRoutes.find(r => r.day === dayOfWeek);
    const crewRoute = todaysRoutes?.routes?.find(r => r.crewId === filters.selectedCrew?._id);
    const currentMiles = crewRoute?.totalMilesDriven || 0;
    
    return {
      totalMiles: currentMiles + estimatedAdditionalMiles,
      additionalMiles: estimatedAdditionalMiles,
      isEstimate: true
    };
  };
  
  // Helper to calculate distance between two points
  const calculateDistance = (point1, point2) => {
    const lat1 = point1.latitude || point1.location?.coordinates?.[1];
    const lng1 = point1.longitude || point1.location?.coordinates?.[0];
    const lat2 = point2.latitude || point2.location?.coordinates?.[1];
    const lng2 = point2.longitude || point2.location?.coordinates?.[0];
    
    if (!lat1 || !lng1 || !lat2 || !lng2) return 0;
    
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Calculate route with a new site added
  const calculateRouteWithSiteNoDebug = useCallback(async (existingStops, newSite) => {
    try {
      const allStops = [...existingStops, newSite];
      
      // Get the current route data for the selected crew
      const dayOfWeek = moment(selectedDate).format('dddd');
      const todaysRoutes = weeklyRoutes.find(r => r.day === dayOfWeek);
      const crewRoute = todaysRoutes?.routes?.find(r => r.crewId === filters.selectedCrew?._id);
      
      const optimizedRoute = await routeService.optimizeRoute(allStops, crewRoute);
      
      if (optimizedRoute?.stops) {
        activeRouteDetails.current = optimizedRoute;
        return optimizedRoute;
      }
      
      return null;
    } catch (error) {
      console.error('Failed to optimize route:', error);
      notificationService.warning('Route optimization failed. Using default ordering.');
      return null;
    }
  }, [routeService, notificationService, selectedDate, weeklyRoutes, filters.selectedCrew]);
  
  const handleClickAssign = useCallback(async (job) => {
    console.log('handleClickAssign...', job)
    
    // Clear previous route details when selecting a new job
    activeRouteDetails.current = null;
    
    const newActiveMarker = {
      ...job,
      isUnassigned: true
    };
    
    dispatch(setActiveMarker(newActiveMarker));
    
    // Update map center
    const offset = filters.selectedCrew ? 
      ROUTE_CONFIG.MAP.OFFSET : 
      ROUTE_CONFIG.MAP.OFFSET_UNASSIGNED;
    
    const coords = extractCoordinates(job);
    if (coords) {
      dispatch(updateUI({
        center: {
          lat: coords.lat - offset,
          lng: coords.lng
        },
        zoomLevel: ROUTE_CONFIG.MAP.ZOOM_LEVELS.OVERVIEW
      }));
    }
    
    // Get site details
    await getDetailsAboutSite(job);
    
    // If crew is selected, calculate route impact
    if (filters.selectedCrew && filteredMarkers.length > 0) {
      console.log('🚗 === NEW JOB CALCULATION ===');
      console.log('🚗 Job being added:', job.name, job._id);
      console.log('🚗 Calculating route with selected crew:', filters.selectedCrew.name);
      
      // Get current route data
      const dayOfWeek = moment(selectedDate).format('dddd');
      const currentRoute = weeklyRoutes.find(r => r.day === dayOfWeek)
        ?.routes.find(r => r.crewId === filters.selectedCrew._id);
      
      const currentMiles = currentRoute?.totalMilesDriven || 0;
      const currentStops = currentRoute?.stops?.length || 0;
      
      console.log('🚗 Current route:', {
        miles: currentMiles,
        stops: currentStops,
        crewId: filters.selectedCrew._id
      });
      
      // Calculate optimized route with new job
      const routeWithNewJob = await calculateRouteWithSite(filteredMarkers, newActiveMarker);
      
      if (routeWithNewJob) {
        const newTotalMiles = routeWithNewJob.totalMilesDriven || 0;
        const additionalMiles = newTotalMiles - currentMiles;
        
        console.log('🚗 Route optimization result:', {
          newTotalMiles,
          currentMiles,
          additionalMiles,
          newStopCount: routeWithNewJob.stops?.length
        });
        
        // Store both total and additional miles WITH calculatedFor
        activeRouteDetails.current = {
          ...routeWithNewJob,
          originalMiles: currentMiles,
          totalMilesDriven: newTotalMiles,
          additionalMiles: Math.max(0, additionalMiles),
          calculatedForSite: job._id // Fixed property name
        };
      }
    } else {
      console.log('🚗 No crew selected or no existing markers');
    }
  }, [dispatch, filters.selectedCrew, filteredMarkers, getDetailsAboutSite, calculateRouteWithSite, weeklyRoutes, selectedDate]);
  // Handle clicking on a job to assign
  const handleClickAssignOld = useCallback(async (job) => {
    console.log('handleClickAssign...', job)
    const newActiveMarker = {
      ...job,
      isUnassigned: true
    };
    
    dispatch(setActiveMarker(newActiveMarker));
    
    // Update map center
    const offset = filters.selectedCrew ? 
      ROUTE_CONFIG.MAP.OFFSET : 
      ROUTE_CONFIG.MAP.OFFSET_UNASSIGNED;
    
    const coords = extractCoordinates(job);
    if (coords) {
      dispatch(updateUI({
        center: {
          lat: coords.lat - offset,
          lng: coords.lng
        },
        zoomLevel: ROUTE_CONFIG.MAP.ZOOM_LEVELS.OVERVIEW
      }));
    }
    
    // Get site details
    await getDetailsAboutSite(job);
    
    // If crew is selected, calculate route impact including this new job
    if (filters.selectedCrew && filteredMarkers.length > 0) {
      const routeWithNewJob = await calculateRouteWithSite(filteredMarkers, newActiveMarker);
      if (routeWithNewJob) {
        // Store the optimized route details
        activeRouteDetails.current = routeWithNewJob;
        
        // The routeWithNewJob should contain totalMilesDriven for the optimized route
        // We need to calculate the DIFFERENCE in miles
        const currentRoute = weeklyRoutes.find(r => r.day === moment(selectedDate).format('dddd'))
          ?.routes.find(r => r.crewId === filters.selectedCrew._id);
        
        const currentMiles = currentRoute?.totalMilesDriven || 0;
        const newTotalMiles = routeWithNewJob.totalMilesDriven || 0;
        const additionalMiles = newTotalMiles - currentMiles;
        
        // Update activeRouteDetails with the mile difference
        activeRouteDetails.current = {
          ...routeWithNewJob,
          additionalMiles: additionalMiles
        };
      }
    }
  }, [dispatch, filters.selectedCrew, filteredMarkers, getDetailsAboutSite, calculateRouteWithSite, weeklyRoutes, selectedDate]);

  const handleClickAssignOLD = useCallback(async (job) => {
    console.log('handleClickAssign...', job)
    const newActiveMarker = {
      ...job,
      isUnassigned: true
    };
    
    dispatch(setActiveMarker(newActiveMarker));
    
    // Update map center
    const offset = filters.selectedCrew ? 
      ROUTE_CONFIG.MAP.OFFSET : 
      ROUTE_CONFIG.MAP.OFFSET_UNASSIGNED;
    
    const coords = extractCoordinates(job);
    if (coords) {
      dispatch(updateUI({
        center: {
          lat: coords.lat - offset,
          lng: coords.lng
        },
        zoomLevel: ROUTE_CONFIG.MAP.ZOOM_LEVELS.OVERVIEW
      }));
    }
    
    // Get site details
    const details = await getDetailsAboutSite(job);
    console.log('Got our details...368', details)
    // If crew is selected, calculate route impact
    if (filters.selectedCrew && filteredMarkers.length > 0) {
      await calculateRouteWithSite(filteredMarkers, newActiveMarker);
    }
  }, [dispatch, filters.selectedCrew, filteredMarkers, getDetailsAboutSite, calculateRouteWithSite]);
  
  // Handle date click from job details
  const handleDateClick = useCallback((date) => {
    dispatch(updateUI({ selectedDate: date }));
    fetchRoutes(date);
  }, [dispatch, fetchRoutes]);
  
  // Handle clicking on a nearby site
  const handleClickOnSiteNearby = useCallback(async (site) => {
    dispatch(setLoading(true));
    dispatch(updateUI({ 
      highlightedNearbySite: site._id,
      highlightedSite: site._id 
    }));
    
    // Find next matching date for this site
    const nextDate = findNextMatchingDate(site);
    
    // Find the crew for this site
    const crew = crews.find(c => c._id === site.crew);
    if (crew) {
      dispatch(selectCrew(crew));
    }
    
    // Update date and fetch routes
    dispatch(updateUI({ selectedDate: nextDate }));
    await fetchRoutes(nextDate);
  }, [dispatch, crews, fetchRoutes]);
  
  // Fetch opposite week data for planning
  const fetchOppositeWeekData = useCallback(async (date, crewId) => {
    const currentWeek = moment(date).week() % 2 === 0 ? 'Even' : 'Odd';
    const weeksToAdd = currentWeek === 'Even' ? 1 : 1;
    const oppositeWeekDate = moment(date).add(weeksToAdd, 'week');
    
    try {
      const data = await routeService.fetchWeeklyRoutes(oppositeWeekDate.toDate());
      return data;
    } catch (error) {
      console.error('Failed to fetch opposite week data:', error);
      return null;
    }
  }, [routeService]);
    
  return {
    // State
    loading,
    unassignedJobs,
    filteredMarkers,
    allSitesMarkers,
    activeRouteDetails: activeRouteDetails.current,
    ui,
    filters,
    sites,
    crews,
    weeklyHours,
    weeklyRoutes,
    state,
    selectedDate,
    dispatch,
    // SSE Assignment
    sseProgress,
    isAssigning,
    // Actions
    fetchRoutes,
    handleAssignToCrew,
    handleUnassignFromCrew,
    handleClickAssign,
    handleDateClick,
    handleClickOnSiteNearby,
    getDetailsAboutSite,
    calculateRouteWithSite,
    fetchOppositeWeekData,
    jobSiteDetails: state.jobSiteDetails,
  };
};
